import React, { useEffect, useReducer, useCallback, createContext} from 'react';
import { authorize, refresh, revoke, getUserinfo, getUserProfile } from './authorizationService';
import { configureTokenInterceptor, configure401Interceptor } from '../utilities';

const OIDCConfig = {
    issuer: process.env.REACT_APP_OIDC_ISSUER,
    clientId: process.env.REACT_APP_OIDC_CLIENT_ID,
    redirectUri: process.env.REACT_APP_OIDC_REDIRECT_URL,
    scopes: process.env.REACT_APP_OIDC_SCOPES,
}


const initialState = {
    isAuthorized: true,
    accessToken: undefined,
    refreshToken: undefined,
    isLoading: true,
    user: {
        email: "test@gmail.com",
        roles: [],
        id: 1,
        profile: {
            stableName: "Admin",
            profilePicturePath: "test.jpg",
            firstName: "Admin",
            lastName: "User"
        },
    },
    tokenInterceptorEject: () => {},
    status401InterceptorEject: () => {},
    logout: ()=> {},

}

const ACTIONS = {
    AUTHORIZATION_CALL: 'AUTHORIZATION_CALL',
    AUTHORIZATION_SUCCESS: 'AUTHORIZATION_SUCCESS',
    AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
    REFRESH_CALL: 'REFRESH_CALL',
    REFRESH_SUCCESS: 'REFRESH_SUCCESS',
    REFRESH_ERROR: 'REFRESH_ERROR',
    USERINFO_CALL: 'USERINFO_CALL',
    USERINFO_SUCCESS: 'USERINFO_SUCCESS',
    USERINFO_ERROR: 'USERINFO_ERROR',
    USER_PROFILE_CALL: 'USER_PROFILE_CALL',
    USER_PROFILE_SUCCESS: 'USER_PROFILE_SUCCESS',
    USER_PROFILE_ERROR: 'USER_PROFILE_ERROR',
    UPDATE_401_INTERCEPTOR: 'UPDATE_401_INTERCEPTOR',
    LOGOUT_CALL: 'LOGOUT_CALL'
}

function reducer(state, action) {
    switch(action.type) {
        case ACTIONS.AUTHORIZATION_CALL:
            return {
                ...state
            };
        case ACTIONS.AUTHORIZATION_SUCCESS:
            state.tokenInterceptorEject();
            localStorage.setItem('refresh_token', action.data.refreshToken);
            return {
                ...state,
                isAuthorized: true,
                accessToken: action.data.accessToken,
                refreshToken: action.data.refreshToken,
                tokenInterceptorEject: configureTokenInterceptor(action.data.accessToken)
            };
        case ACTIONS.REFRESH_CALL:
            return {
                ...state
            };
        case ACTIONS.REFRESH_SUCCESS:
            state.tokenInterceptorEject();
            localStorage.setItem('refresh_token', action.data.refreshToken);
            return {
                ...state,
                isAuthorized: true,
                accessToken: action.data.accessToken,
                refreshToken: action.data.refreshToken,
                tokenInterceptorEject: configureTokenInterceptor(action.data.accessToken)
            };
        case ACTIONS.USERINFO_CALL:
            return {
                ...state
            };
        case ACTIONS.USERINFO_SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    ...action.data,
                    id: action.data.sub
                }
            };
        case ACTIONS.USER_PROFILE_CALL:
            return {
                ...state
            };
        case ACTIONS.USER_PROFILE_SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    ...action.data
                }
            }
        case ACTIONS.UPDATE_401_INTERCEPTOR:
            state.status401InterceptorEject();
            return {
                ...state,
                status401InterceptorEject: action.data
            }
        case ACTIONS.LOGOUT_CALL:
            revoke(OIDCConfig, state.accessToken)
            .then(response => {
                revoke(OIDCConfig, state.refreshToken)
                .then( response => {
                    localStorage.removeItem('refresh_token');
                })
                .catch(error => console.log(error));
            })
            .catch(error => console.log(error));
            return {
                ...initialState,
                logout: state.logout
            }
        default:
            throw new Error(`No action of type ${action.type} found in reducer.`);
    }
}

export const AuthorizationContext = createContext(initialState);

function AuthorizationProvider(props) {

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        logout: handleLogout
    });

    const handleAuthorization = useCallback(() => {
        dispatch({ type: ACTIONS.AUTHORIZATION_CALL });
        authorize(OIDCConfig)
        .then(response => {
            if(response) {
                dispatch({ type: ACTIONS.AUTHORIZATION_SUCCESS, data: response });
            }
        });
    }, []);

    const handleRefresh = useCallback((refreshToken) => {
        dispatch({ type: ACTIONS.REFRESH_CALL });
        refresh(OIDCConfig, refreshToken)
        .then(response => dispatch({ type: ACTIONS.REFRESH_SUCCESS, data: response }))
        .catch(() => handleAuthorization());
    }, [handleAuthorization]);

    const handleUserinfo = useCallback(() => {
        dispatch({ type: ACTIONS.USERINFO_CALL });
        getUserinfo(OIDCConfig)
        .then(response => dispatch({ type: ACTIONS.USERINFO_SUCCESS, data: response.data }))
        .catch(error => console.error(error)); //TODO: Handle error better
    }, []);

    const handleUserProfile = useCallback(() => {
        dispatch({ type: ACTIONS.USER_PROFILE_CALL });
        getUserProfile(state.user.id)
        .then(response => dispatch({ type: ACTIONS.USER_PROFILE_SUCCESS, data: response.data }));
    }, [state.user.id]);

    useEffect(() => {
        if (!state.isAuthorized) {
            let refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                handleRefresh(refreshToken);
            } else {
                handleAuthorization();
            }
        } else {
            if (!state.user.id) {
                handleUserinfo();
            } else if (!state.user.profile) {
                handleUserProfile();
            }
        }
    }, [state.isAuthorized, state.user, handleUserinfo, handleAuthorization, handleRefresh, handleUserProfile]);

    const attemptRefresh = useCallback(() => {
        if (state.refreshToken) {
            handleRefresh(state.refreshToken)
        }
    }, [state.refreshToken, handleRefresh]);

    useEffect(() => {
        dispatch({ type: ACTIONS.UPDATE_401_INTERCEPTOR, data: configure401Interceptor(attemptRefresh)});
    }, [attemptRefresh, state.refreshToken])

    function handleLogout() {
        dispatch({ type: ACTIONS.LOGOUT_CALL });
    }

    return (
        <AuthorizationContext.Provider value={state} {...props}>
            {state.isAuthorized && props.children}
            
        </AuthorizationContext.Provider>
    );
}

export default AuthorizationProvider;